import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config';
import { validateCreateUser } from './validation/validators';
import { handleAuthError } from './handlers/authError';
import type { CreateUserParams, AuthResult } from './types';

export async function createUser(params: CreateUserParams): Promise<AuthResult> {
  try {
    // Store current admin auth state if exists
    const currentUser = auth.currentUser;

    // Validate input
    const validatedInput = validateCreateUser(params);
    
    // Create Firebase auth user
    const { user } = await createUserWithEmailAndPassword(
      auth,
      validatedInput.email,
      validatedInput.password
    );

    // Initialize user document with roles
    const roles = [
      {
        role: 'customer',
        assignedAt: new Date(),
        assignedBy: currentUser?.uid || 'system',
        isActive: true
      }
    ];

    // Add volunteer role if isVolunteer is true
    if (validatedInput.isVolunteer) {
      roles.push({
        role: 'volunteer',
        assignedAt: new Date(),
        assignedBy: currentUser?.uid || 'system',
        isActive: true
      });
    }

    const userData = {
      id: user.uid,
      email: validatedInput.email,
      firstNameEn: validatedInput.firstNameEn,
      lastNameEn: validatedInput.lastNameEn,
      firstNameAr: validatedInput.firstNameAr,
      lastNameAr: validatedInput.lastNameAr,
      phone: validatedInput.phone,
      roles,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    try {
      await setDoc(doc(db, 'users', user.uid), userData);
      
      // Restore admin session if we had one
      if (currentUser) {
        await auth.updateCurrentUser(currentUser);
      }

      return { user, userData };
    } catch (error) {
      // Cleanup: delete auth user if Firestore fails
      await user.delete();
      throw error;
    }
  } catch (error) {
    throw handleAuthError(error);
  }
}